import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import Footer from '@/components/Footer';
import { StatApprovalInterface } from '@/components/stats/StatApprovalInterface';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { useUserRole } from '@/hooks/useUserRole';
import { calculateAge } from '@/utils/ageUtils';
import { CheckCircle, Users, BarChart3 } from 'lucide-react';

export default function ScoreApprovalsNew() {
  const { user, profile } = useAuthenticatedUser();
  const { permissions } = useUserRole();

  // Determine user's age to enforce age restrictions
  const userAge = useMemo(() => {
    if (!profile) return null;
    
    // First try to calculate from birth_date if available
    if (profile.birth_date) {
      return calculateAge(new Date(profile.birth_date));
    }
    
    // Fallback to age_group field
    if (profile.age_group) {
      // Handle legacy age group values
      if (profile.age_group === '13-and-under' || parseInt(profile.age_group) <= 13) {
        return 13;
      }
      if (profile.age_group === '14-17') {
        return 16; // Use middle of range
      }
      if (profile.age_group === '18-plus') {
        return 18;
      }
    }
    
    return null;
  }, [profile]);

  // Determine which tabs to show based on user role, permissions, and age
  const availableTabs = useMemo(() => {
    const tabs = [];
    
    // Age restriction: Under 13 users only see player tab
    if (userAge !== null && userAge < 13) {
      tabs.push({ value: 'player', label: 'As Player' });
      return tabs;
    }
    
    // Add coach tab if user has coach permissions - don't add player tab for coaches
    if (permissions.isCoach || permissions.canManageTeams) {
      tabs.push({ value: 'coach', label: 'As Coach' });
    }
    
    // Add team owner tab if user is team owner
    if (permissions.isTeamOwner) {
      tabs.push({ value: 'team-owner', label: 'As Team Owner' });
    }
    
    // Add venue tab if user is venue
    if (permissions.isVenue || permissions.canManageVenues) {
      tabs.push({ value: 'venue', label: 'As Venue' });
    }
    
    // Only add player tab if user doesn't have other specific roles
    if (!permissions.isCoach && !permissions.canManageTeams && !permissions.isTeamOwner && !permissions.isVenue && !permissions.canManageVenues) {
      tabs.push({ value: 'player', label: 'As Player' });
    }
    
    return tabs;
  }, [permissions, userAge]);

  // Set default tab to the first available tab (always "player")
  const defaultTab = availableTabs.length > 0 ? availableTabs[0].value : 'player';

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <div className="hidden md:block">
          <Header />
        </div>
        <div className="md:hidden">
          <MobileHeader />
        </div>
        <div className="container mx-auto px-4 py-8 pt-20">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">Please log in to view score approvals</p>
            </CardContent>
          </Card>
        </div>
        <MobileBottomNav />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="hidden md:block">
        <Header />
      </div>
      <div className="md:hidden">
        <MobileHeader />
      </div>
      
      <div className="container mx-auto px-4 py-8 pt-20 pb-24 md:pb-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Score Approvals</h1>
            <p className="text-muted-foreground">
              Review and approve player statistics and game scores
            </p>
          </div>

          {/* Overview Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <CheckCircle className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">-</p>
                    <p className="text-sm text-muted-foreground">Pending Approvals</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Users className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">-</p>
                    <p className="text-sm text-muted-foreground">Players Reviewed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <BarChart3 className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">-</p>
                    <p className="text-sm text-muted-foreground">Stats Approved</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Approval Interfaces */}
          {availableTabs.length > 0 ? (
            <Tabs defaultValue={defaultTab} className="space-y-6">
              {/* Mobile Layout: Vertical Stack */}
              <TabsList className="flex flex-col space-y-2 h-auto bg-background p-2 md:hidden">
                {availableTabs.map((tab) => (
                  <TabsTrigger 
                    key={tab.value}
                    value={tab.value}
                    className="w-full justify-center py-3 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Desktop Layout: Dynamic Grid */}
              <TabsList className={`hidden md:grid w-full ${
                availableTabs.length === 1 ? 'grid-cols-1' :
                availableTabs.length === 2 ? 'grid-cols-2' :
                availableTabs.length === 3 ? 'grid-cols-3' :
                'grid-cols-4'
              }`}>
                {availableTabs.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {availableTabs.some(tab => tab.value === 'player') && (
                <TabsContent value="player" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Your Player Stats</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <StatApprovalInterface userRole="player" />
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {availableTabs.some(tab => tab.value === 'coach') && (
                <TabsContent value="coach" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Team Player Stats - Coach Review</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <StatApprovalInterface userRole="coach" />
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {availableTabs.some(tab => tab.value === 'team-owner') && (
                <TabsContent value="team-owner" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Team Player Stats - Owner Review</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <StatApprovalInterface userRole="team-owner" />
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {availableTabs.some(tab => tab.value === 'venue') && (
                <TabsContent value="venue" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Game Stats - Venue Approval</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <StatApprovalInterface userRole="venue" />
                    </CardContent>
                  </Card>
                </TabsContent>
              )}
            </Tabs>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">No approval interfaces available for your account type.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <MobileBottomNav />
      <Footer />
    </div>
  );
}